import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface SEOData {
  meta_title: string | null;
  meta_description: string | null;
}

interface SEOHeadProps {
  /** page_seo lookup key. Defaults to the current pathname. */
  path?: string;
  /**
   * Route this page should canonicalize to when that differs from the URL being
   * viewed. Only for genuinely duplicated content: a type='post' row also
   * resolves at /news/<slug>, but /blog/<slug> is the page built for it.
   * Leave unset to self-canonicalize.
   */
  canonicalPath?: string;
  /**
   * Soft 404 / do-not-index page: emits <meta name="robots" content="noindex">
   * and emits NO canonical at all. Never derive this from data that can be
   * missing for transient reasons (see the isError guard in PostDetail.tsx) —
   * a noindex on a live page is a deindex.
   */
  noindex?: boolean;
  fallbackTitle?: string;
  fallbackDescription?: string;
}

/**
 * The only host this site may ever claim as canonical.
 *
 * Hardcoded on purpose, NOT window.location.origin: https://www.rioplex.com/
 * answers 200 with the same app, and every Vercel preview deployment serves it
 * too. A self-referencing origin would let each of those hosts canonicalize to
 * itself instead of consolidating on the apex. The cost — localhost and preview
 * builds emit production canonicals — is the desired SEO behaviour.
 */
const CANONICAL_ORIGIN = "https://rioplex.com";

/**
 * React Router matches routes case-insensitively and ignores trailing slashes,
 * and vercel.json rewrites every path to index.html, so /About, /about/ and
 * /about// are all live 200s rendering the same page. Fold them onto one
 * spelling before it becomes a canonical URL, otherwise this component would
 * mint a fresh self-canonical duplicate for every casing a crawler finds.
 *
 * Lowercasing is safe for slugs: every admin write path forces them through
 * .toLowerCase().replace(/[^a-z0-9-]/g, "").
 */
const normalizePath = (pathname: string): string => {
  const trimmed = pathname.toLowerCase().replace(/\/+$/, "");
  return trimmed || "/";
};

/**
 * Private / authenticated routes: never indexed, never canonicalized. Mirrors
 * the X-Robots-Tag entries in vercel.json — change both or neither. Matched
 * against the normalized (lowercased) path, because React Router will happily
 * render /Admin/users while a case-sensitive check would miss it.
 */
const PRIVATE_PATH_PREFIXES = [
  "/admin",
  "/admin-login",
  "/portal",
  "/forgot-password",
  "/reset-password",
  "/auth",
];

const isPrivatePath = (normalizedPath: string): boolean =>
  PRIVATE_PATH_PREFIXES.some(
    (prefix) => normalizedPath === prefix || normalizedPath.startsWith(`${prefix}/`)
  );

const fetchSEO = async (path: string): Promise<SEOData | null> => {
  const { data, error } = await supabase
    .from("page_seo")
    .select("meta_title, meta_description")
    .eq("page_path", path)
    .maybeSingle();

  if (error) throw error;
  return data;
};

const SEOHead = ({
  path,
  canonicalPath,
  noindex = false,
  fallbackTitle = "RioPlex - Gateway to the Future",
  fallbackDescription = "RioPlex is a strategic initiative uniting the Rio Grande Valley to attract Foreign Direct Investment and drive industrial growth.",
}: SEOHeadProps) => {
  const location = useLocation();
  const viewedPath = normalizePath(location.pathname);
  // page_seo lookup key. Normalized, so a visitor on /About or /about/ still
  // resolves the '/about' row.
  const currentPath = normalizePath(path || location.pathname);
  const isPrivate = isPrivatePath(viewedPath);
  const suppressIndexing = noindex || isPrivate;
  // Canonical comes from the URL actually being viewed, not from `path` (which
  // is only the page_seo lookup key), unless a page explicitly points elsewhere.
  // A crawler that lands on /invest and follows the client-side <Navigate>
  // therefore emits canonical /invest/sectors.
  const canonicalUrl = suppressIndexing
    ? null
    : `${CANONICAL_ORIGIN}${normalizePath(canonicalPath || location.pathname)}`;

  // React Query handles caching, dedup across components, and stale-while-revalidate.
  // Per-page SEO rarely changes, so we keep it warm for 5 minutes.
  const { data: seoData } = useQuery({
    queryKey: ["page-seo", currentPath],
    queryFn: () => fetchSEO(currentPath),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 1,
  });

  useEffect(() => {
    const title = seoData?.meta_title || fallbackTitle;
    const description = seoData?.meta_description || fallbackDescription;

    document.title = title;

    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement("meta");
      metaDescription.setAttribute("name", "description");
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute("content", description);

    const updateMetaTag = (property: string, content: string) => {
      let tag =
        document.querySelector(`meta[property="${property}"]`) ||
        document.querySelector(`meta[name="${property}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(property.startsWith("og:") ? "property" : "name", property);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };

    updateMetaTag("og:title", title);
    updateMetaTag("og:description", description);
    updateMetaTag("twitter:title", title);
    updateMetaTag("twitter:description", description);

    // ── robots ──
    // This component is the only writer of <meta name="robots"> for public
    // routes and it has no unmount cleanup, so the non-suppressed branch has to
    // actively remove a tag left behind by an earlier client-side navigation.
    const existingRobots = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
    if (suppressIndexing) {
      let robots = existingRobots;
      if (!robots) {
        robots = document.createElement("meta");
        robots.setAttribute("name", "robots");
        document.head.appendChild(robots);
      }
      robots.setAttribute("content", isPrivate ? "noindex, nofollow" : "noindex");
    } else {
      existingRobots?.remove();
    }

    // ── canonical + og:url ──
    // Absolute, per route. A relative canonical would resolve against whatever
    // host served the page, which is the defect being fixed. Suppression REMOVES
    // the tags instead of skipping them, so a soft 404 or an admin page can
    // never inherit the canonical of a route visited earlier in the session.
    const existingCanonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    const existingOgUrl = document.querySelector<HTMLMetaElement>('meta[property="og:url"]');

    if (canonicalUrl === null) {
      existingCanonical?.remove();
      existingOgUrl?.remove();
    } else {
      // Update in place — a second <link rel="canonical"> makes Google ignore both.
      let canonical = existingCanonical;
      if (!canonical) {
        canonical = document.createElement("link");
        canonical.setAttribute("rel", "canonical");
        document.head.appendChild(canonical);
      }
      canonical.setAttribute("href", canonicalUrl);
      updateMetaTag("og:url", canonicalUrl);
    }
  }, [
    seoData,
    fallbackTitle,
    fallbackDescription,
    canonicalUrl,
    suppressIndexing,
    isPrivate,
  ]);

  return null;
};

export default SEOHead;
